import {List, Map, OrderedMap} from 'immutable';

import RESTClient from 'web-app/api/clients/rest-client-famly-app';
import Child from 'web-app/react/entities/children/model';
import Contact from 'web-app/react/entities/contacts/model';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import {track} from 'web-app/util/famlytics/index';

import InquiryAction from './inquiry-action-model';
import Inquiry from './inquiry-model';
import {type ExtendedInquiry, type FetchInquiriesParams} from './types';

const getRelations = relations => {
    const childIds = Object.keys(relations);
    const relationsLists = Map(childIds.map(id => [id, List(relations[id])]));
    const mappedRelations = relationsLists.map(relations => relations.map(rel => Contact.fromAPI(rel)));

    return mappedRelations.map(relations => Map(relations.map(rel => [rel.relationId, rel])));
};

export const fetchInquiries = (institutionId: string, params?: FetchInquiriesParams) => {
    return RESTClient.getAuthenticatedObservable('v2/inquiries', {
        institutionId,
        ...params,
    }).map(response => ({
        behaviors: new BehaviorsCollection(response.behaviors),
        inquiries: OrderedMap<string, ExtendedInquiry>(
            response.inquiries.map((inquiry: ExtendedInquiry) => [
                inquiry.inquiryId,
                Inquiry.fromAPI({
                    ...inquiry,
                    // the fte value is actually associated to the child and not the inquiry itself
                    // this can be 0 but also be undefined or (uncalculated)
                    // in that case we just want to fallback to null
                    fte: response.children.find(child => child.childId === inquiry.childId)?.fte ?? null,
                }),
            ]),
        ),
        children: List(response.children).map(child => Child.fromAPI(child)),
        relationMaps: getRelations(response.relations),
        csv: response.csv,
        total: response.total,
        nextOffset: response.nextOffset,
        lost: response.lost,
        enrolled: response.enrolled,
        showarounds: response.showarounds,
    }));
};

export const fetchInquirySignupLink = (institutionId: string) => {
    return RESTClient.getAuthenticatedObservable('v2/inquiries/signup/link', {institutionId}).map(response => ({
        link: response.link,
    }));
};

const mapInquiryToEntities = (inquiry, child, relations) => ({
    inquiry: Inquiry.fromAPI(inquiry),
    child: Child.fromAPI(child),
    relations: Map(List(relations).map((rel: any) => [rel.relationId, Contact.fromAPI(rel)])),
});

export const createInquiry = (institutionId: string, inquiry) => {
    return RESTClient.postAuthenticatedObservable('v2/inquiries', {
        ...inquiry,
        institutionId,
        name: inquiry.childName,
    }).map(response => {
        const inquiryFromResponse = response.inquiry as ExtendedInquiry;
        track(TrackingEvents.INQUIRIES_NEW_INQUIRY_CREATED, {
            reason: inquiryFromResponse.reason,
            source: inquiryFromResponse.source,
            bookingSource: inquiryFromResponse.bookingSource,
        });
        return mapInquiryToEntities(response.inquiry, response.child, response.relations);
    });
};

export const startIn = (inquiryId, data) => {
    return RESTClient.postAuthenticatedObservable(`v2/inquiries/${inquiryId}/confirm`, data).map(response => ({
        inquiry: Inquiry.fromAPI(response.inquiry),
    }));
};

export const updateInquiry = (inquiry: ExtendedInquiry) => {
    return RESTClient.postAuthenticatedObservable(`v2/inquiries/${inquiry.inquiryId}`, {
        status: inquiry.status,
        source: inquiry.source ? inquiry.source : '',
        bookingSource: inquiry.bookingSource ? inquiry.bookingSource : '',
        reason: inquiry.reason ? inquiry.reason : '',
        note: inquiry.note,
        lostReason: inquiry.lostReason ? inquiry.lostReason : '',
        lostCategory: inquiry.lostCategory ? inquiry.lostCategory : '',
        groupId: inquiry.groupId,
    }).map(response => ({
        inquiry: Inquiry.fromAPI(response.inquiry),
    }));
};

export const deleteInquiry = (inquiryId: string) => {
    return RESTClient.deleteAuthenticatedObservable(`v2/inquiries/${inquiryId}`).map(() => inquiryId);
};

/**
 * @deprecated Use `app/react/staffing-attendance/registration/inquiries/details/activity-timeline/activity-modal/inquiry-activity-rest-calls.ts`
 */
export const createInquiryAction = (inquiryId: string, inquiryAction) => {
    return RESTClient.postAuthenticatedObservable(`v2/inquiries/${inquiryId}/actions`, {
        ...inquiryAction,
    }).map(response => InquiryAction.fromAPI(response.action));
};
